import React, { useState, useCallback, useMemo } from 'react';
import { Eye, EyeOff, Target } from 'lucide-react';

interface AggregatedClick {
  x: number;
  y: number;
  rank: number;
  product_id?: string | null;
  product_label?: string | null;
  count?: number; // How many respondents clicked this exact spot
}

interface BoundingBox {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ProductStats {
  product_id: string;
  label: string;
  totalClicks: number;
  rank1Clicks: number;
  rank2Clicks: number;
  rank3Clicks: number;
  avgRank: number;
}

interface ImageHotspotHeatmapProps {
  imageUrl: string;
  clicks: AggregatedClick[];
  boundingBoxes?: BoundingBox[];
  totalRespondents?: number;
  className?: string;
}

const ImageHotspotHeatmap: React.FC<ImageHotspotHeatmapProps> = ({
  imageUrl,
  clicks,
  boundingBoxes = [],
  totalRespondents = 1,
  className = '',
}) => {
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [showBoxes, setShowBoxes] = useState(true);
  const [showClicks, setShowClicks] = useState(true);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.clientWidth, height: img.clientHeight });
  };

  const normalizedToPixel = useCallback(
    (nx: number, ny: number) => ({
      x: nx * imageSize.width,
      y: ny * imageSize.height,
    }),
    [imageSize],
  );

  // Calculate product statistics
  const productStats = useMemo((): ProductStats[] => {
    const stats = new Map<string, ProductStats>();

    clicks.forEach((click) => {
      if (!click.product_id) return;

      const existing = stats.get(click.product_id) || {
        product_id: click.product_id,
        label: click.product_label || click.product_id,
        totalClicks: 0,
        rank1Clicks: 0,
        rank2Clicks: 0,
        rank3Clicks: 0,
        avgRank: 0,
      };

      existing.totalClicks++;
      if (click.rank === 1) existing.rank1Clicks++;
      if (click.rank === 2) existing.rank2Clicks++;
      if (click.rank === 3) existing.rank3Clicks++;

      stats.set(click.product_id, existing);
    });

    // Calculate average rank
    return Array.from(stats.values())
      .map((stat) => {
        const totalRankSum = stat.rank1Clicks * 1 + stat.rank2Clicks * 2 + stat.rank3Clicks * 3;
        const totalRankedClicks = stat.rank1Clicks + stat.rank2Clicks + stat.rank3Clicks;
        return {
          ...stat,
          avgRank: totalRankedClicks > 0 ? totalRankSum / totalRankedClicks : 0,
        };
      })
      .sort((a, b) => b.totalClicks - a.totalClicks);
  }, [clicks]);

  // Calculate heatmap intensity for bounding boxes
  const boxIntensities = useMemo(() => {
    const intensities = new Map<string, { total: number; byRank: Record<number, number> }>();

    boundingBoxes.forEach((box) => {
      intensities.set(box.id, { total: 0, byRank: {} });
    });

    clicks.forEach((click) => {
      if (click.product_id && intensities.has(click.product_id)) {
        const existing = intensities.get(click.product_id)!;
        existing.total++;
        existing.byRank[click.rank] = (existing.byRank[click.rank] || 0) + 1;
      }
    });

    return intensities;
  }, [clicks, boundingBoxes]);

  // Get heatmap color based on intensity
  const getHeatmapColor = (intensity: number, maxIntensity: number): string => {
    if (maxIntensity === 0) return 'rgba(107, 114, 128, 0.1)';
    const normalizedIntensity = intensity / maxIntensity;

    // Color gradient: gray -> teal -> green
    if (normalizedIntensity < 0.33) {
      return `rgba(7, 229, 209, ${0.1 + normalizedIntensity * 0.3})`; // Light teal
    } else if (normalizedIntensity < 0.66) {
      return `rgba(7, 229, 209, ${0.3 + normalizedIntensity * 0.3})`; // Medium teal
    } else {
      return `rgba(16, 185, 129, ${0.4 + normalizedIntensity * 0.4})`; // Strong green
    }
  };

  // Get rank color for click markers
  const getRankColor = (rank: number): string => {
    const colors: Record<number, string> = {
      1: '#07E5D1', // Primary teal
      2: '#3B82F6', // Blue
      3: '#F59E0B', // Amber
      4: '#EF4444', // Red
      5: '#8B5CF6', // Purple
    };
    return colors[rank] || '#6B7280';
  };

  const maxIntensity = Math.max(...Array.from(boxIntensities.values()).map((v) => v.total), 1);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setShowBoxes(!showBoxes)}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            showBoxes
              ? 'bg-teal-50 border-teal-200 text-teal-700'
              : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}
        >
          {showBoxes ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          Product Regions
        </button>
        <button
          onClick={() => setShowClicks(!showClicks)}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            showClicks
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}
        >
          {showClicks ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          Click Points
        </button>
      </div>

      {/* Image with heatmap overlay */}
      <div className="relative inline-block w-full">
        <img
          src={imageUrl}
          alt="Hotspot heatmap visualization"
          onLoad={handleImageLoad}
          className="max-w-full h-auto rounded-lg border border-gray-200"
        />

        {/* Bounding box heatmap overlays */}
        {showBoxes &&
          boundingBoxes.map((box) => {
            const pos = normalizedToPixel(box.x, box.y);
            const size = { x: box.width * imageSize.width, y: box.height * imageSize.height };
            const intensity = boxIntensities.get(box.id)?.total || 0;
            const percentage =
              totalRespondents > 0 ? Math.round((intensity / totalRespondents) * 100) : 0;

            return (
              <div
                key={box.id}
                className="absolute border-2 border-white/50 transition-all"
                style={{
                  left: pos.x,
                  top: pos.y,
                  width: size.x,
                  height: size.y,
                  backgroundColor: getHeatmapColor(intensity, maxIntensity),
                }}
              >
                {/* Label with percentage */}
                <div className="absolute -top-6 left-0 text-xs px-2 py-0.5 rounded bg-gray-900/80 text-white whitespace-nowrap flex items-center gap-1">
                  <span className="font-medium">{box.label}</span>
                  <span className="text-gray-300">({percentage}%)</span>
                </div>

                {/* Click count badge */}
                {intensity > 0 && (
                  <div className="absolute bottom-1 right-1 text-xs px-1.5 py-0.5 rounded bg-white/90 text-gray-800 font-semibold shadow-sm">
                    {intensity} clicks
                  </div>
                )}
              </div>
            );
          })}

        {/* Individual click markers */}
        {showClicks &&
          clicks.map((click, idx) => {
            const pos = normalizedToPixel(click.x, click.y);
            return (
              <div
                key={`${click.x}-${click.y}-${click.rank}-${idx}`}
                className="absolute w-3 h-3 rounded-full pointer-events-none transform -translate-x-1.5 -translate-y-1.5 border border-white shadow-sm"
                style={{
                  left: pos.x,
                  top: pos.y,
                  backgroundColor: getRankColor(click.rank),
                  opacity: 0.7,
                }}
                title={`Rank ${click.rank}: ${click.product_label || 'Unknown'}`}
              />
            );
          })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span className="font-medium">Rank Colors:</span>
          {[1, 2, 3].map((rank) => (
            <div key={rank} className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: getRankColor(rank) }}
              />
              <span className="text-xs">{rank === 1 ? '1st' : rank === 2 ? '2nd' : '3rd'}</span>
            </div>
          ))}
        </div>
        <div className="text-gray-400">|</div>
        <div className="flex items-center gap-1">
          <Target className="w-4 h-4 text-gray-400" />
          <span>
            {clicks.length} total clicks from {totalRespondents} respondents
          </span>
        </div>
      </div>

      {/* Product stats table */}
      {productStats.length > 0 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-700">Product Selection Summary</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Total
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    1st Pick
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    2nd Pick
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    3rd Pick
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Avg Rank
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {productStats.map((stat, idx) => (
                  <tr key={stat.product_id} className={idx === 0 ? 'bg-teal-50/50' : ''}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{stat.label}</td>
                    <td className="px-4 py-2 text-sm text-center text-gray-700 font-semibold">
                      {stat.totalClicks}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-teal-600">
                      {stat.rank1Clicks || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-blue-600">
                      {stat.rank2Clicks || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-amber-600">
                      {stat.rank3Clicks || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-gray-600">
                      {stat.avgRank.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageHotspotHeatmap;
